import { ArrowLeft, RotateCcw, Sparkles, Wand2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { PRODUCTS } from '@/data/products';
import { filterProducts } from '@/features/catalog/filterProducts';
import { EMPTY_FILTERS } from '@/features/catalog/catalog.types';
import { cn } from '@/lib/cn';
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery';

import { Container } from '../common/Container';
import { PawIcon } from '../common/icons';
import { SectionHeading } from '../common/Section';
import { Button } from '../ui/button';

interface QuizOption {
  value: string;
  label: string;
  emoji: string;
  hint?: string;
}

interface QuizStep {
  /** URL param this step contributes to. */
  param: 'pet' | 'stage' | 'need' | 'line';
  question: string;
  options: QuizOption[];
}

const STEPS: QuizStep[] = [
  {
    param: 'pet',
    question: 'Primeiro: quem vai comer?',
    options: [
      { value: 'dog', label: 'Um cão', emoji: '🐶' },
      { value: 'cat', label: 'Um gato', emoji: '🐱' },
    ],
  },
  {
    param: 'stage',
    question: 'Qual a fase de vida dele?',
    options: [
      { value: 'puppy', label: 'Filhote', emoji: '🍼', hint: 'até 12 meses' },
      { value: 'adult', label: 'Adulto', emoji: '🦴', hint: '1 a 7 anos' },
      { value: 'senior', label: 'Sênior', emoji: '💛', hint: '7 anos ou mais' },
    ],
  },
  {
    param: 'need',
    question: 'O que é mais importante para você?',
    options: [
      {
        value: '',
        label: 'Melhor custo-benefício',
        emoji: '💰',
        hint: 'sem abrir mão da qualidade',
      },
      { value: 'castrated', label: 'Ele é castrado', emoji: '✂️', hint: 'controle de peso' },
      { value: 'sensitive', label: 'Digestão sensível', emoji: '🌿', hint: 'fórmula leve' },
      { value: 'natural', label: 'Fórmula natural', emoji: '🍃', hint: 'sem corantes' },
    ],
  },
];

/**
 * "Acha-Ração" — a three-tap route into the catalogue.
 *
 * Filters are powerful but intimidating: the panel has eight groups. This asks
 * three plain-language questions instead and hands the visitor a pre-filtered
 * result, which is the same destination reached without reading a single
 * filter label.
 */
export function PetMatchQuiz() {
  const navigate = useNavigate();
  const reduceMotion = usePrefersReducedMotion();
  const [answers, setAnswers] = useState<string[]>([]);

  const stepIndex = answers.length;
  const isDone = stepIndex >= STEPS.length;
  const currentStep = STEPS[stepIndex];

  /** Live count so the final screen can promise a real number. */
  const matchCount = useMemo(() => {
    if (!isDone) return 0;

    return filterProducts(PRODUCTS, {
      ...EMPTY_FILTERS,
      pet: answers[0] ? ([answers[0]] as never) : [],
      stage: answers[1] ? ([answers[1]] as never) : [],
      need: answers[2] ? ([answers[2]] as never) : [],
    }).length;
  }, [answers, isDone]);

  function buildQuery(): string {
    const params = new URLSearchParams();
    STEPS.forEach((step, index) => {
      const value = answers[index];
      if (value) params.append(step.param, value);
    });
    // No specific need selected means "best value" — sort by price per kilo.
    if (!answers[2]) params.set('sort', 'price-per-kg');
    return params.toString();
  }

  const transition = reduceMotion ? { duration: 0 } : { duration: 0.25, ease: 'easeOut' as const };

  return (
    <section className="bg-paw-pattern bg-brown-900 py-14 lg:py-20">
      <Container size="md">
        <SectionHeading
          inverse
          eyebrow="Acha-Ração"
          title="Não sabe qual escolher? Responda 3 perguntas."
          description="Em menos de 15 segundos a gente filtra o catálogo inteiro para o perfil do seu pet."
          className="mb-8"
        />

        <div className="relative overflow-hidden rounded-3xl border-2 border-caramel-500/25 bg-white p-6 shadow-float lg:p-8">
          {/* Progress */}
          <div className="mb-6 flex items-center justify-center gap-2">
            {STEPS.map((step, index) => (
              <span
                key={step.param}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300',
                  index < stepIndex ? 'w-10 bg-orange-500' : 'w-6 bg-sand-300',
                  index === stepIndex && 'w-10 bg-caramel-400',
                )}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {!isDone && currentStep ? (
              <motion.div
                key={stepIndex}
                initial={{ opacity: 0, x: reduceMotion ? 0 : 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: reduceMotion ? 0 : -24 }}
                transition={transition}
                className="flex flex-col gap-5"
              >
                <h3 className="text-center font-display text-xl font-extrabold text-brown-900 lg:text-2xl">
                  {currentStep.question}
                </h3>

                <div
                  className={cn(
                    'grid gap-3',
                    currentStep.options.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2',
                  )}
                >
                  {currentStep.options.map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setAnswers((current) => [...current, option.value])}
                      className={cn(
                        'group flex items-center gap-3 rounded-2xl border-2 border-sand-300 bg-white p-4 text-left hover:border-orange-500 hover:bg-orange-100/40',
                        'transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]',
                      )}
                    >
                      <span
                        className="grid size-12 shrink-0 place-items-center rounded-xl bg-cream-200 text-2xl transition-transform duration-300 group-hover:scale-110"
                        aria-hidden="true"
                      >
                        {option.emoji}
                      </span>

                      <span>
                        <span className="block font-display text-base font-bold text-brown-900">
                          {option.label}
                        </span>
                        {option.hint && (
                          <span className="block text-xs text-brown-700">{option.hint}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>

                {stepIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => setAnswers((current) => current.slice(0, -1))}
                    className="mx-auto inline-flex items-center gap-1.5 text-sm font-semibold text-brown-700 transition-colors hover:text-brown-900"
                  >
                    <ArrowLeft className="size-4" aria-hidden="true" />
                    Voltar
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={transition}
                className="flex flex-col items-center gap-4 text-center"
              >
                <span className="grid size-16 place-items-center rounded-2xl bg-orange-100">
                  <Sparkles className="size-8 text-orange-500" aria-hidden="true" />
                </span>

                <h3 className="font-display text-2xl font-extrabold text-brown-900">
                  {matchCount > 0
                    ? `Encontramos ${matchCount} ${matchCount === 1 ? 'opção' : 'opções'} para o seu pet!`
                    : 'Temos opções para o seu pet!'}
                </h3>

                <p className="max-w-md text-sm text-brown-700">
                  {matchCount > 0
                    ? 'Já deixamos o catálogo filtrado. É só escolher o tamanho e adicionar à sua lista.'
                    : 'Nem tudo do nosso estoque está no site. Chame no WhatsApp que a gente encontra para você.'}
                </p>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <Button size="lg" onClick={() => navigate(`/catalogo?${buildQuery()}`)}>
                    <PawIcon />
                    Ver as opções
                  </Button>

                  <Button variant="outline" size="lg" onClick={() => setAnswers([])}>
                    <RotateCcw aria-hidden="true" />
                    Refazer
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <Wand2
            className="pointer-events-none absolute -right-4 -bottom-4 size-32 text-caramel-500/10"
            aria-hidden="true"
          />
        </div>
      </Container>
    </section>
  );
}
